import {
  PIN_CARD_MAX_WIDTH,
  PIN_CARD_ELEMENT_WIDTH_REFERENCE,
  PAGE_ONLY_ELEMENT_WIDTH,
} from 'constants/cardLayout';
import { nudgeAwayFromOccupied } from './suggestPinPlacement';
import { getYouTubeVideoFromActivity } from './youtubeUrl';

const PIN_WIDTH = 300;
const PIN_HEIGHT = 400;
const PIN_MIN_WIDTH = 200;
const PIN_MAX_WIDTH = PIN_CARD_MAX_WIDTH;
const PIN_MIN_HEIGHT = 160;
const PIN_MAX_HEIGHT = 900;
const PIN_HEADER_HEIGHT = 64;
const PIN_MAX_IMAGE_HEIGHT = 560;
const LEGACY_PIN_HEIGHT = 180;
const GRID_GAP = 24;
const INBOX_ORIGIN = { x: 80, y: 80 };
const GRID_COL_STRIDE = PIN_MAX_WIDTH + GRID_GAP;

export function getPinnedComment(activity) {
  const content = activity?.content?.[0];
  const comments = content?.comments;
  if (!Array.isArray(comments) || !comments.length) return null;
  const pinnedId = activity?.action?.commentId;
  if (pinnedId) {
    const pinned = comments.find(c => String(c._id) === String(pinnedId));
    if (pinned) return pinned;
  }
  return comments[0];
}

export function getHighlightDimensions(activity) {
  const pinned = getPinnedComment(activity);
  const commentData = pinned?.highlightData;
  let elementWidth = Number(commentData?.elementWidth);
  let elementHeight = Number(commentData?.elementHeight);

  if (!(elementWidth > 0)) {
    elementWidth = Number(activity?.action?.highlightData?.elementWidth);
  }
  if (!(elementHeight > 0)) {
    elementHeight = Number(activity?.action?.highlightData?.elementHeight);
  }

  // Page-only stubs: always use the current layout constant so changing
  // PAGE_ONLY_ELEMENT_WIDTH updates grid/canvas width without re-saving.
  if (isPageOnlyPin(activity)) {
    elementWidth = PAGE_ONLY_ELEMENT_WIDTH;
    elementHeight = null;
  }

  return {
    elementWidth: elementWidth > 0 ? elementWidth : null,
    elementHeight: elementHeight > 0 ? elementHeight : null,
  };
}

function getScaledImageHeight(activity, cardWidth) {
  if (getBoardYouTubeVideo(activity)) {
    return Math.round(cardWidth * (9 / 16));
  }
  const { elementWidth, elementHeight } = getHighlightDimensions(activity);
  if (!(elementWidth > 0 && elementHeight > 0)) return 120;
  const scaled = elementHeight * (cardWidth / elementWidth);
  return Math.min(scaled, PIN_MAX_IMAGE_HEIGHT);
}

export function getPinCardWidth(activity, placementWidth, { manualResize = false } = {}) {
  if (manualResize && placementWidth) {
    return Math.min(Math.max(placementWidth, PIN_MIN_WIDTH), PIN_MAX_WIDTH);
  }

  const { elementWidth } = getHighlightDimensions(activity);
  if (!(elementWidth > 0)) return PIN_WIDTH;

  const scaled = Math.round(
    elementWidth * (PIN_CARD_MAX_WIDTH / PIN_CARD_ELEMENT_WIDTH_REFERENCE)
  );
  return Math.min(Math.max(scaled, PIN_MIN_WIDTH), PIN_MAX_WIDTH);
}

export function getPinCardSize(activity, placementWidth, options) {
  const width = getPinCardWidth(activity, placementWidth, options);
  const imageHeight = getScaledImageHeight(activity, width);
  const height = Math.min(
    PIN_MAX_HEIGHT,
    Math.max(PIN_MIN_HEIGHT, PIN_HEADER_HEIGHT + imageHeight)
  );
  return { width, height };
}

export function isDefaultPinHeight(height) {
  return !height || height <= LEGACY_PIN_HEIGHT || height === PIN_HEIGHT;
}

export function normalizePlacements(activities, placements) {
  const byId = new Map(activities.map(a => [getActivityId(a), a]));
  return placements.map(placement => {
    const activity = byId.get(String(placement.activityId));
    if (!activity) return placement;

    const { width, height: computedHeight } = getPinCardSize(activity, placement.width, {
      manualResize: Boolean(placement.manualSize),
    });
    if (!isDefaultPinHeight(placement.height)) {
      return { ...placement, width };
    }
    return { ...placement, width, height: computedHeight };
  });
}

export function getPinThumbnail(activity) {
  const action = activity?.action;
  const pinned = getPinnedComment(activity);
  const screenshotSource = action?.elementScreenshotUrl ? action : pinned;
  if (screenshotSource?.elementScreenshotThumbUrl) return screenshotSource.elementScreenshotThumbUrl;
  if (screenshotSource?.elementScreenshotMediumUrl) return screenshotSource.elementScreenshotMediumUrl;
  if (action?.elementScreenshotUrl) return action.elementScreenshotUrl;
  if (pinned?.elementScreenshotUrl) return pinned.elementScreenshotUrl;
  // Pasted YouTube pages often store the site logo as og:image. Use the video frame.
  const youtubeThumb = getYouTubeVideoFromActivity(activity)?.thumbnailUrl;
  if (youtubeThumb) return youtubeThumb;
  if (action?.imgUrl) return action.imgUrl;
  if (pinned?.imgUrl) return pinned.imgUrl;
  const content = activity?.content?.[0];
  if (content?.comments?.[0]?.imgUrl) return content.comments[0].imgUrl;
  const metas = content?.metas || {};
  const og = metas['og:image'] || metas['twitter:image'] || metas.image;
  if (og) {
    if (og.charAt(0) === '/' && content?.urls?.[0]?.host) {
      const host = content.urls[0].host;
      const protocol = content.urls[0].protocol || 'https:';
      return `${protocol}//${host}${og}`;
    }
    return og;
  }
  return null;
}

export function getPinDomain(activity) {
  const url = activity?.content?.[0]?.urls?.[0];
  if (!url) return 'Unknown';
  const hostname = url.hostname || url.host || '';
  return hostname.replace(/^www\./i, '');
}

export function getPinSnippet(activity) {
  const content = activity?.content?.[0];
  const comment = getPinnedComment(activity) || content?.comments?.[0];
  const commentText = String(comment?.text || comment?.body || '').trim();
  if (commentText) return commentText;
  const metas = content?.metas || {};
  const description =
    metas.description || metas['og:description'] || metas['twitter:description'] || '';
  if (description) return String(description).trim();
  return String(content?.title || '').trim();
}

export function isPageOnlyPin(activity) {
  const comment = getPinnedComment(activity);
  const highlight = comment?.highlightData || activity?.action?.highlightData || {};
  return highlight.saveScope === 'page' || highlight.selectionTextSource === 'page';
}

/** Page-only YouTube paste: a video card, not a highlighted selection on the watch page. */
export function getBoardYouTubeVideo(activity) {
  if (!isPageOnlyPin(activity)) return null;
  return getYouTubeVideoFromActivity(activity);
}

export function getActivityId(activity) {
  return String(activity?._id || activity?.actionId || '');
}

export function autoLayoutUnplacedPins(activities, existingPlacements = []) {
  const placedIds = new Set(
    existingPlacements.map(p => String(p.activityId)).filter(Boolean)
  );
  const unplaced = activities.filter(a => !placedIds.has(getActivityId(a)));
  if (!unplaced.length) {
    return normalizePlacements(activities, existingPlacements);
  }

  // Prefer extending the existing cluster (connectivity) over a fixed top-left inbox.
  let originX = INBOX_ORIGIN.x;
  let originY = INBOX_ORIGIN.y;
  if (existingPlacements.length) {
    let maxRight = -Infinity;
    let sumY = 0;
    for (const placement of existingPlacements) {
      const width = Number(placement.width) || PIN_WIDTH;
      maxRight = Math.max(maxRight, (Number(placement.x) || 0) + width);
      sumY += Number(placement.y) || 0;
    }
    originX = maxRight + GRID_GAP;
    originY = sumY / existingPlacements.length;
  }

  const occupiedRects = existingPlacements.map(placement => ({
    x: Number(placement.x) || 0,
    y: Number(placement.y) || 0,
    w: Number(placement.width) || PIN_WIDTH,
    h: Number(placement.height) || PIN_HEIGHT,
  }));

  const newPlacements = unplaced.map((activity, index) => {
    const { width, height } = getPinCardSize(activity);
    const gridX = originX + (index % 3) * GRID_COL_STRIDE;
    const gridY = originY + Math.floor(index / 3) * (PIN_HEIGHT + GRID_GAP);
    const position = nudgeAwayFromOccupied(
      { x: gridX, y: gridY },
      occupiedRects,
      index,
      { w: width, h: height }
    );
    const placement = {
      activityId: getActivityId(activity),
      x: position.x,
      y: position.y,
      width,
      height,
      zIndex: existingPlacements.length + index,
    };
    occupiedRects.push({ x: placement.x, y: placement.y, w: width, h: height });
    return placement;
  });

  return normalizePlacements(activities, [...existingPlacements, ...newPlacements]);
}

export function mergeBoardState(activities, board) {
  const placements = autoLayoutUnplacedPins(activities, board?.placements || []);
  return {
    ...board,
    placements,
  };
}

export {
  PIN_WIDTH,
  PIN_HEIGHT,
  LEGACY_PIN_HEIGHT,
  GRID_GAP,
  INBOX_ORIGIN,
};
